import { Database, Radio } from "lucide-react";
import type { ReactNode } from "react";

import type { DataOrigin, Meta } from "@busright/data-contract";

import { relativeFromNow } from "@/lib/format";

// Maps recency of `dataAsOf` to freshness buckets for the header pill. Color LAW: RAG-red is
// reserved for health/risk only, so a stale/offline snapshot never renders red. Fresh data keeps
// the calm "fresh" treatment; anything >2 days old gets a neutral-AMBER caution treatment; an
// unknown / missing `dataAsOf` (typical for the offline snapshot) gets a NEUTRAL treatment.
const HOUR_MS = 60 * 60 * 1000;
const FRESH_LIMIT = 24 * HOUR_MS;
const AMBER_LIMIT = 2 * 24 * HOUR_MS;

const freshnessClass = (dataAsOf: string | null): "fresh" | "amber" | "neutral" => {
  if (!dataAsOf) {
    return "neutral";
  }
  const age = Date.now() - new Date(dataAsOf).getTime();
  if (Number.isNaN(age)) {
    return "neutral";
  }
  if (age <= FRESH_LIMIT) {
    return "fresh";
  }
  // >2 days old is worth a caution flag (amber); 1–2 days stays neutral.
  return age > AMBER_LIMIT ? "amber" : "neutral";
};

const originLabel = (origin: DataOrigin): string =>
  origin === "LIVE_BQ" ? "Live BigQuery" : "Offline snapshot";

export interface FreshnessPillProps {
  readonly meta: Meta | undefined;
}

// Header freshness pill. Shows origin (live vs snapshot) + how stale `dataAsOf` is — the
// command-center "never lies about it" contract.
export const FreshnessPill = ({ meta }: FreshnessPillProps): ReactNode => {
  if (!meta) {
    return (
      <span className="pill">
        <span className="dot" /> No data
      </span>
    );
  }
  const cls = freshnessClass(meta.dataAsOf);
  const Icon = meta.origin === "LIVE_BQ" ? Radio : Database;
  return (
    <span
      className={`pill ${cls}`}
      title={`${originLabel(meta.origin)} — data as of ${meta.dataAsOf ?? "unknown"}`}
    >
      <Icon size={13} aria-hidden />
      {originLabel(meta.origin)} · {relativeFromNow(meta.dataAsOf)}
    </span>
  );
};
